// Contratar o cambiar el plan de UNA marca (facturación por marca) —
// ADR-2026-10-28-elegir-plan-de-marca. Selector de nivel + pago:
//
//  0. Los niveles (Uso básico/medio/intensivo, por debajo Ultra 1/2/3) se muestran con las
//     cards del catálogo de planes (`PlanCard`, `PlanCarousel` en el teléfono) —
//     ADR-2026-10-28-elegir-plan-de-marca-con-el-catalogo.
//  1. El dueño elige el nivel → `suscribirMarca` / `cambiarPlanDeMarca` cobran con la tarjeta
//     guardada de la agencia.
//  2. Si el payment server contesta `needs_checkout` (no hay tarjeta guardada o Rebill la
//     rechazó por datos), se monta `RebillCardPayment` con la metadata de la marca.
//  3. El alta y el cambio los aplica SÓLO el webhook: se espera a ver el plan en la billetera
//     (`esperarPlanDeMarca`) antes de decir "listo", y se avisa `atlas:credits-updated`.
//
// Un miembro (no dueño) ve el estado y "Pedile al admin": no puede pagar. Qué se ofrece
// en cada caso lo decide `vistaDelPlanDeMarca` (puro, testeado).
//
// Lo usan el aviso de "sin créditos" (`SubscriptionLimitModal`), el host global
// (`HostDelPlanDeMarca`) y, después, Configuración → Plan y el alta de marca.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { cuentaYPermisos } from '../../lib/permisosDelEquipo';
import { esperarPlanDeMarca, leerBilleterasDeMarcas, type BilleteraDeMarca } from '../../lib/saldo';
import { cambiarPlanDeMarca, estadoDeMarcas, suscribirMarca, type ResultadoPlanDeMarca } from '../../lib/paymentServer';
import { REBILL_PLAN_IDS } from '../../config';
import { useAperturaAnimada } from '../../hooks/useAperturaAnimada';
import RebillCardPayment from '../RebillCardPayment';
import { PlanCard } from '../pricing/PlanCard';
import { PlanCarousel } from '../pricing/PlanCarousel';
import { SubscribeButton } from '../pricing/PricingControls';
import { borrarClaveDeIdempotencia, leerOCrearClaveDeIdempotencia } from '../../lib/claveDeIdempotencia';
import {
  TOPE_TARJETA_ENVIADA_MS,
  alCerrarElPlan,
  alSalirDelCheckout,
  periodoComoTexto,
  tarjetaEnCamino,
  borrarClaveTrasEsperar,
  cardsDePlanDeMarca,
  claveDeCobroDeMarca,
  esPlanDePruebaDeMarca,
  etiquetaDelBotonDePlanDeMarca,
  lineaDePlanDeMarca,
  precioDePlanDeMarcaComoTexto,
  siguientePasoTrasCobro,
  vistaDelPlanDeMarca,
  recargaActivable,
  type ModoDelPlanDeMarca,
  type OpcionDePlanDeMarca,
  type PlanDeMarca,
} from './planDeMarca';
import RecargaAutomatica from './RecargaAutomatica';
import { recargaDeBilletera, recargaDeEstado, topeDelMesAlcanzado, type RecargaDeMarca } from './recargaDeMarca';

export interface ElegirPlanDeMarcaProps {
  /** Se dibuja mientras `true`; al pasar a `false` sale con su transición. */
  abierto?: boolean;
  /** `null` = la empresa factura por marca pero no hay marca activa resuelta: se muestra el error. */
  brandId: string | null;
  /** Pista de dónde se abrió; lo que se ofrece lo decide el estado de la billetera. */
  modo: ModoDelPlanDeMarca;
  /** El plan ya quedó aplicado (o el pago entró y el webhook está por aplicarlo). */
  onListo: () => void;
  /** Cerrar sin cambios. Si falta, se usa `onListo`. Tras un cobro, cerrar siempre es `onListo`. */
  onCerrar?: () => void;
  /** Una línea más debajo del aviso de la tarjeta (p. ej. qué pasa si se cierra sin pagar). */
  nota?: string;
  /**
   * Se abrió porque lo pedido no alcanzó (el "sin créditos" de `SubscriptionLimitModal`): se trata
   * como sin créditos aunque quede algo de saldo, y se ofrece activar la recarga automática.
   */
  sinCreditos?: boolean;
  /**
   * Al terminar de pagar, mostrar la recarga automática de la marca (el primer plan la deja activada,
   * ADR-0044 del payment server). `false` en el alta de una marca nueva: la pantalla de "lista" ya la muestra.
   */
  recargaAlTerminar?: boolean;
}

type Contexto = {
  userId: string;
  idCompany: string;
  email: string | null;
  nombre: string | null;
  esDueno: boolean;
  /** Empresa interna: ve los planes de prueba (ADR-2026-10-28-planes-de-prueba-de-marca). */
  devTester: boolean;
  marca: string;
  billetera: BilleteraDeMarca | undefined;
  /** La recarga automática de la marca, por RLS. null = no se pudo leer: no se ofrece. */
  recarga: RecargaDeMarca | null;
};

type Fase =
  | { tipo: 'cargando' }
  | { tipo: 'error_de_carga' }
  | { tipo: 'sin_marca' }
  | { tipo: 'vista' }
  | { tipo: 'cobrando'; plan: PlanDeMarca }
  | { tipo: 'checkout'; plan: PlanDeMarca; metadata: Record<string, string> }
  | { tipo: 'esperando'; plan: PlanDeMarca; verificando: boolean }
  | { tipo: 'listo'; plan: PlanDeMarca; confirmado: boolean }
  /** El cobro pudo haber salido y el plan no apareció: sin botón de pagar otra vez. */
  | { tipo: 'sin_confirmar' };

// Mismo TTL que el cambio de plan one-click (`RebillChangePlanConfirm`).
const TTL_CLAVE_MS = 30 * 60 * 1000;

async function cargarContexto(brandId: string): Promise<Contexto> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('sin sesión');
  const [{ data: perfil }, { data: marca }, permisos, billeteras, recargaPorRls] = await Promise.all([
    supabase.from('profiles').select('id_company, name, email').eq('id', user.id).maybeSingle(),
    supabase.from('brand_profiles').select('name').eq('id', brandId).maybeSingle(),
    cuentaYPermisos(),
    leerBilleterasDeMarcas([brandId]),
    leerRecarga(brandId),
  ]);
  // Al dueño se le puede ofrecer activarla: necesita las recargas del mes, que sólo trae /marca/estado.
  const recarga = permisos.esDueno ? ((await leerRecargaDelDueno(user.id, brandId)) ?? recargaPorRls) : recargaPorRls;
  const p = perfil as { id_company?: string | null; name?: string | null; email?: string | null } | null;
  if (!p?.id_company) throw new Error('sin empresa');
  // Sólo decide si se MUESTRAN los planes de prueba: el payment server los rechaza a cualquier otra.
  const { data: empresa } = await supabase
    .from('company_profiles')
    .select('dev_tester')
    .eq('id_company', p.id_company)
    .maybeSingle();
  return {
    userId: user.id,
    idCompany: p.id_company,
    email: p.email ?? user.email ?? null,
    nombre: p.name?.trim() || null,
    esDueno: permisos.esDueno,
    devTester: (empresa as { dev_tester?: boolean | null } | null)?.dev_tester === true,
    marca: (marca as { name?: string } | null)?.name ?? 'Esta marca',
    billetera: billeteras.get(brandId),
    recarga,
  };
}

/**
 * La recarga de la marca según /marca/estado (sólo el dueño), con las recargas del mes: con el tope
 * alcanzado, activarla no trae créditos (review Greptile #227). null si falla: queda la de RLS.
 */
async function leerRecargaDelDueno(userId: string, brandId: string): Promise<RecargaDeMarca | null> {
  // Es un dato opcional: si el servidor de pagos tarda, la ventana no se queda cargando
  // (review Greptile #227). Pasado el tope sigue con la recarga leída por RLS.
  let tope: number | undefined;
  const sinRespuesta = new Promise<null>((resolve) => {
    tope = window.setTimeout(() => resolve(null), TOPE_ESTADO_MS);
  });
  try {
    const leida = estadoDeMarcas(userId).then((r) =>
      r.ok ? recargaDeEstado(r.marcas.find((m) => m.brand_id === brandId)?.recarga) : null,
    );
    return await Promise.race([leida, sinRespuesta]);
  } catch {
    return null;
  } finally {
    window.clearTimeout(tope);
  }
}

const TOPE_ESTADO_MS = 3000;

/** La recarga de la marca, tolerante: si falla la lectura, la ventana sigue sin ofrecerla. */
async function leerRecarga(brandId: string): Promise<RecargaDeMarca | null> {
  const { data, error } = await supabase
    .from('billeteras_de_marca')
    .select('recarga_activa, recarga_creditos, recarga_umbral, recarga_pausada_motivo')
    .eq('brand_id', brandId)
    .maybeSingle();
  if (error || !data) return null;
  return recargaDeBilletera(data as Parameters<typeof recargaDeBilletera>[0]);
}

export default function ElegirPlanDeMarca({
  abierto = true,
  brandId,
  modo,
  onListo,
  onCerrar,
  nota,
  sinCreditos = false,
  recargaAlTerminar = true,
}: ElegirPlanDeMarcaProps) {
  const { montado, visible } = useAperturaAnimada(abierto, 150);
  const [ctx, setCtx] = useState<Contexto | null>(null);
  const [fase, setFase] = useState<Fase>({ tipo: 'cargando' });
  const [elegido, setElegido] = useState<PlanDeMarca | null>(null);
  const [error, setError] = useState<string | null>(null);
  // El dueño activó la recarga desde acá: en vez de ofrecerla, se avisa que los créditos vienen.
  const [recargaActivada, setRecargaActivada] = useState<RecargaDeMarca | null>(null);
  // Rebill está procesando la tarjeta (desde su `submit` hasta el éxito o el rechazo): no se
  // puede cerrar en el medio.
  const [procesandoTarjeta, setProcesandoTarjeta] = useState(false);
  // Cada apertura (o cambio de marca) es una sesión nueva: lo que llega tarde de una anterior
  // (un `onSuccess` de Rebill, la respuesta del cobro, la espera del webhook) no escribe.
  const sesion = useRef(0);
  const vivo = useRef(true);
  // Hubo un `tarjeta_enviada` sin rechazo en esta apertura: pudo haber cobrado aunque el candado
  // ya se haya soltado, así que cerrar ya no es "sin cambios" (`alCerrarElPlan`).
  const tarjetaEnviada = useRef(false);
  // Lo mismo, para dibujar: el botón de abajo del checkout verifica en vez de volver a los planes.
  const [hayTarjetaEnviada, setHayTarjetaEnviada] = useState(false);

  // `tarjeta_enviada` también sale en los submits que el SDK frena antes de mandar (sin éxito
  // ni error después): el candado se suelta solo al tope. No hay evidencia de que un pedido
  // haya salido, así que cerrar después es cerrar (si sí cobró, el webhook aplica el plan).
  useEffect(() => {
    if (!procesandoTarjeta) return;
    const t = window.setTimeout(() => setProcesandoTarjeta(false), TOPE_TARJETA_ENVIADA_MS);
    return () => window.clearTimeout(t);
  }, [procesandoTarjeta]);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  useEffect(() => {
    if (!abierto) {
      // Cerrado: lo que llegue tarde de esta apertura ya no es de nadie.
      sesion.current++;
      return;
    }
    const mia = ++sesion.current;
    setError(null);
    setRecargaActivada(null);
    setProcesandoTarjeta(false);
    tarjetaEnviada.current = false;
    setHayTarjetaEnviada(false);
    if (!brandId) {
      setFase({ tipo: 'sin_marca' });
      return;
    }
    setFase({ tipo: 'cargando' });
    cargarContexto(brandId)
      .then((c) => {
        if (mia !== sesion.current) return;
        setCtx(c);
        setFase({ tipo: 'vista' });
      })
      .catch(() => {
        if (mia === sesion.current) setFase({ tipo: 'error_de_carga' });
      });
  }, [abierto, brandId]);

  const vista = ctx
    ? vistaDelPlanDeMarca({
        esDueno: ctx.esDueno,
        plan: ctx.billetera?.plan ?? null,
        disponibles: ctx.billetera?.disponibles ?? 0,
        modo,
        devTester: ctx.devTester,
        sinCreditos,
        recargaActivable:
          !!ctx.recarga && recargaActivable(ctx.billetera, ctx.recarga.activa) && !topeDelMesAlcanzado(ctx.recarga),
      })
    : null;
  const opciones: OpcionDePlanDeMarca[] = vista?.tipo === 'elegir' ? vista.opciones : [];
  // Uso básico/medio/intensivo van como las cards del catálogo de planes; los de prueba
  // (sólo `dev_tester`), en filas chicas debajo (ADR-2026-10-28-elegir-plan-de-marca-con-el-catalogo).
  const cards = vista?.tipo === 'elegir' ? cardsDePlanDeMarca(vista.opciones, vista.accion) : [];
  const dePrueba = opciones.filter((o) => esPlanDePruebaDeMarca(o.id));
  // La card en foco del carrusel del teléfono: la que cobra el botón de abajo.
  const enFoco = cards.find((c) => c.key === elegido) ?? cards[0] ?? null;

  const ocupado = fase.tipo === 'cobrando' || fase.tipo === 'esperando' || procesandoTarjeta;
  const cobrandoPlan = fase.tipo === 'cobrando' ? fase.plan : null;
  // Después de un cobro (listo o sin confirmar) o con una tarjeta enviada sin respuesta, cerrar
  // es `onListo`, nunca `onCerrar` ("sin cambios"): el alta de una marca nueva descarta su
  // borrador con `onCerrar`, y borrarlo con un pago en camino dejaría el cobro sin marca
  // (ADR-2026-10-28-marca-nueva-con-pago, -2). La ref se lee al cerrar, no al dibujar.
  const faseTipo = fase.tipo;
  const cerrar = useCallback(() => {
    if (ocupado) return;
    const como = alCerrarElPlan({ fase: faseTipo, tarjetaEnviada: tarjetaEnviada.current });
    (como === 'listo' ? onListo : (onCerrar ?? onListo))();
  }, [ocupado, faseTipo, onCerrar, onListo]);

  useEffect(() => {
    if (!abierto) return;
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [abierto, cerrar]);

  /** Espera a que el webhook deje el plan en la billetera. `verificando` = el cobro fue ambiguo. */
  const esperarElPlan = async (
    mia: number,
    marca: string,
    plan: PlanDeMarca,
    verificando: boolean,
    storageKey?: string,
  ) => {
    setFase({ tipo: 'esperando', plan, verificando });
    const confirmado = await esperarPlanDeMarca(marca, plan);
    window.dispatchEvent(new Event('atlas:credits-updated'));
    // La clave se borra aunque el modal ya sea de otra sesión: la operación terminó.
    if (storageKey && borrarClaveTrasEsperar({ verificando, confirmado })) borrarClaveDeIdempotencia(storageKey);
    if (!vivo.current || mia !== sesion.current) return;
    if (confirmado || !verificando) setFase({ tipo: 'listo', plan, confirmado });
    else setFase({ tipo: 'sin_confirmar' });
    // Con el plan aplicado, la recarga ya quedó como la dejó el webhook: se relee para mostrarla. Va DESPUÉS
    // de "Listo" y sin esperarla: es opcional, y una lectura colgada no puede dejar al dueño trabado en la
    // espera después de pagar (review Greptile #228). Mientras tanto se oculta la que había antes del pago.
    if (confirmado && recargaAlTerminar && ctx?.esDueno) {
      setCtx((c) => (c ? { ...c, recarga: null } : c));
      void (async () => {
        const recarga = (await leerRecargaDelDueno(ctx.userId, marca)) ?? (await leerRecarga(marca));
        if (vivo.current && mia === sesion.current) setCtx((c) => (c ? { ...c, recarga } : c));
      })().catch(() => undefined);
    }
  };

  const pagar = async (plan: PlanDeMarca) => {
    // Con una tarjeta enviada sin respuesta no se cobra otra vez en esta apertura (`alSalirDelCheckout`).
    if (!ctx || !brandId || vista?.tipo !== 'elegir' || ocupado || tarjetaEnviada.current) return;
    const mia = sesion.current;
    setError(null);
    setFase({ tipo: 'cobrando', plan });
    // La clave sobrevive a cerrar y reabrir (y a otra pestaña): un reintento de la misma
    // intención cae en la misma operación del payment server.
    let accion = vista.accion;
    for (;;) {
      const storageKey = claveDeCobroDeMarca(ctx.userId, brandId, accion, plan);
      const args = { userId: ctx.userId, brandId, plan, idempotencyKey: leerOCrearClaveDeIdempotencia(storageKey, TTL_CLAVE_MS) };
      let r: ResultadoPlanDeMarca | 'ambiguo';
      try {
        r = accion === 'suscribir' ? await suscribirMarca(args) : await cambiarPlanDeMarca(args);
      } catch {
        r = 'ambiguo';
      }
      const paso = siguientePasoTrasCobro(r, REBILL_PLAN_IDS, { accion });
      // Antes de mirar si la sesión sigue: un resultado definitivo libera la clave igual.
      if (paso.borrarClave) borrarClaveDeIdempotencia(storageKey);
      if (!vivo.current || mia !== sesion.current) return;
      switch (paso.tipo) {
        case 'reintentar_como_alta':
          // La billetera tiene `plan` pero no una sub viva: para el payment server no tiene plan.
          accion = 'suscribir';
          continue;
        case 'confirmar':
          return void esperarElPlan(mia, brandId, plan, false);
        case 'verificar':
          // 5xx o red: pudo haber cobro. Nada de "probá de nuevo": se mira la billetera.
          return void esperarElPlan(mia, brandId, plan, true, storageKey);
        case 'checkout':
          setFase({ tipo: 'checkout', plan: paso.plan, metadata: paso.metadata });
          return;
        case 'error':
          setError(paso.mensaje);
          setFase({ tipo: 'vista' });
          return;
      }
    }
  };

  if (!montado) return null;

  // La sesión en la que se dibujó el checkout: sus eventos sólo valen mientras siga siendo la actual.
  const sesionDelRender = sesion.current;

  const saliendo = !abierto;
  const claseOverlay = `fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm transition-opacity ease-out ${
    visible ? 'opacity-100 duration-300' : 'opacity-0 duration-150'
  }`;
  // Eligiendo plan, en la compu el panel se abre al ancho de las cards (una columna por
  // nivel); el resto de los pasos sigue angosto. El ancho entra en la transición.
  const eligiendo = !!ctx && vista?.tipo === 'elegir' && (fase.tipo === 'vista' || fase.tipo === 'cobrando');
  const anchoDelPanel = !eligiendo
    ? 'max-w-md'
    : cards.length >= 3
      ? 'max-w-md lg:max-w-[1000px]'
      : cards.length === 2
        ? 'max-w-md lg:max-w-[720px]'
        : 'max-w-md';
  const columnas = cards.length >= 3 ? 'lg:grid-cols-3' : cards.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-1';
  const clasePanel = `relative flex max-h-[calc(100dvh-32px)] w-full ${anchoDelPanel} flex-col overflow-hidden rounded-lg bg-white shadow-[0_24px_64px_rgba(16,24,45,0.22)] transition-[opacity,transform,filter,max-width] ease-out ${
    visible
      ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0'
      : `opacity-0 duration-150 motion-safe:blur-[4px] ${saliendo ? 'motion-safe:-translate-y-3' : 'motion-safe:translate-y-3'}`
  }`;
  const linea = ctx ? lineaDePlanDeMarca(ctx.billetera) : null;

  // Sin créditos y con la recarga desactivada, el dueño puede activarla acá mismo
  // (ADR-2026-10-29-sin-creditos-ofrece-la-recarga). Al activarla, el servidor recarga en su próxima
  // corrida (≤ 1 minuto): la oferta se cambia por ese aviso.
  const ofrecerRecarga = vista && vista.tipo !== 'pedir_al_admin' && vista.ofrecerRecarga;
  const recarga =
    ctx && recargaActivada !== null ? (
      <p role="status" className="animate-paso-titulo mt-5 rounded-md bg-[rgb(var(--brand-primary-rgb)_/_0.085)] px-4 py-3 text-[14px] text-brand-black/85">
        {(ctx.billetera?.disponibles ?? 0) >= recargaActivada.umbral
          ? `Listo, la recarga automática quedó activada. Recarga ${recargaActivada.creditos} créditos cuando a esta marca le queden menos de ${recargaActivada.umbral}.`
          : // Prometer el minuto sólo con el conteo del mes a mano y debajo del tope.
            recargaActivada.recargasDelMes != null && !topeDelMesAlcanzado(recargaActivada)
            ? `Listo, la recarga automática quedó activada. En menos de un minuto se suman ${recargaActivada.creditos} créditos a esta marca.`
            : 'Listo, la recarga automática quedó activada.'}
      </p>
    ) : ctx && ctx.recarga && ofrecerRecarga ? (
      <div className="mt-5">
        <RecargaAutomatica
          brandId={brandId ?? ''}
          recarga={ctx.recarga}
          devTester={ctx.devTester}
          variante="linea"
          onCambio={(r) => {
            setCtx((c) => (c ? { ...c, recarga: r } : c));
            if (r.activa) setRecargaActivada(r);
          }}
        />
      </div>
    ) : null;

  return createPortal(
    <div className={claseOverlay} onClick={cerrar}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-plan-de-marca"
        className={clasePanel}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={cerrar}
          disabled={ocupado}
          aria-label="Cerrar"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-md text-black/40 transition-colors hover:bg-black/[0.04] hover:text-black disabled:opacity-40"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="overflow-y-auto p-5 sm:p-6">
          {fase.tipo === 'cargando' && (
            <div className="flex items-center justify-center gap-2 py-14 text-[15px] text-black/45">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando el plan de la marca…
            </div>
          )}

          {(fase.tipo === 'error_de_carga' || fase.tipo === 'sin_marca') && (
            <div key={fase.tipo} className="animate-paso-adelante py-6">
              <p className="pr-8 text-[17px] font-semibold text-brand-black">
                {fase.tipo === 'sin_marca' ? 'No encontramos la marca activa' : 'No pudimos cargar el plan de la marca'}
              </p>
              <p className="mt-2 text-[15px] text-black/60">
                {fase.tipo === 'sin_marca'
                  ? 'Elegí una marca en el selector de marcas y probá de nuevo.'
                  : 'Revisá tu conexión y probá de nuevo en unos segundos.'}
              </p>
              <BotonPrincipal onClick={cerrar} className="mt-6 w-full">
                Entendido
              </BotonPrincipal>
            </div>
          )}

          {ctx && vista && (fase.tipo === 'vista' || fase.tipo === 'cobrando') && (
            <div key="vista" className="animate-paso-adelante">
              <Encabezado marca={ctx.marca} linea={linea} titulo={vista.titulo} />
              <p className="mt-3 text-[15px] leading-[22px] text-black/65">{vista.mensaje}</p>

              {vista.tipo === 'elegir' && (
                <>
                  {/* Compu: una columna por nivel, cada una con su botón (como la comparativa de
                      planes). Teléfono: las mismas cards deslizables y el botón abajo. */}
                  {cards.length > 0 && (
                    <div className={`mt-6 hidden gap-4 lg:grid ${columnas}`}>
                      {cards.map((c) => (
                        <PlanCard
                          key={c.key}
                          className="h-full"
                          content={c.content}
                          credits={c.credits}
                          generates={c.generates}
                          price={c.price}
                          period={c.period}
                          ivaIncluido={c.ivaIncluido}
                          isCurrent={c.isCurrent}
                          footer={
                            <SubscribeButton
                              label={c.ctaLabel}
                              onClick={() => void pagar(c.key)}
                              disabled={ocupado}
                              loading={cobrandoPlan === c.key}
                            />
                          }
                        />
                      ))}
                    </div>
                  )}
                  {enFoco && (
                    <div className="mt-5 lg:hidden">
                      <PlanCarousel<PlanDeMarca> plans={cards} selected={enFoco.key} onSelect={setElegido} />
                      <div className="mt-3">
                        <SubscribeButton
                          label={enFoco.ctaLabel}
                          onClick={() => void pagar(enFoco.key)}
                          disabled={ocupado}
                          loading={cobrandoPlan === enFoco.key}
                        />
                      </div>
                    </div>
                  )}

                  {dePrueba.length > 0 && (
                    <div className="mt-5">
                      <p className="text-[12px] font-medium text-black/45">Planes de prueba · sólo cuentas internas</p>
                      <div className="mt-2 grid gap-2 lg:grid-cols-2">
                        {dePrueba.map((o) => (
                          <div key={o.id} className="flex items-center gap-3 rounded-md border border-black/[0.09] px-4 py-3">
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="text-[14px] font-semibold text-brand-black">{o.nombre}</span>
                              <span className="text-[12.5px] text-black/50">
                                {o.creditos.toLocaleString('es-AR')} créditos {periodoComoTexto(o.periodo)} ·{' '}
                                {precioDePlanDeMarcaComoTexto(o.precio)} {periodoComoTexto(o.periodo)}
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => void pagar(o.id)}
                              disabled={ocupado}
                              aria-label={etiquetaDelBotonDePlanDeMarca(vista.accion, o.nombre)}
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-[rgb(var(--brand-primary-rgb)_/_0.085)] px-4 py-2 text-[13.5px] font-semibold text-brand-primary transition-[background-color,transform] duration-150 ease-out hover:bg-[rgb(var(--brand-primary-rgb)_/_0.14)] enabled:active:scale-[0.96] disabled:opacity-60"
                            >
                              {cobrandoPlan === o.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                              {vista.accion === 'suscribir' ? 'Contratar' : 'Cambiar'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {recarga}

                  {error && (
                    <p role="alert" className="animate-paso-titulo mt-4 rounded-md bg-red-50 px-3 py-2.5 text-[14px] text-red-700">
                      {error}
                    </p>
                  )}

                  <p className="mt-4 text-center text-[12.5px] text-black/45">
                    Se cobra con la tarjeta guardada de la cuenta. Si no hay una, te la pedimos.
                  </p>
                  {nota && <p className="mt-1 text-center text-[12.5px] text-black/45">{nota}</p>}
                </>
              )}

              {vista.tipo === 'tope' && recarga}

              {vista.tipo !== 'elegir' && (
                <BotonPrincipal onClick={cerrar} className="mt-6 w-full">
                  Entendido
                </BotonPrincipal>
              )}
            </div>
          )}

          {ctx && fase.tipo === 'checkout' && (
            <div key="checkout" className="animate-paso-adelante">
              <Encabezado marca={ctx.marca} linea={null} titulo="Cargá la tarjeta para esta marca" />
              <p className="mt-3 text-[15px] text-black/65">
                {(opciones.find((o) => o.id === fase.plan)?.nombre ?? fase.plan)}: el cobro va a nombre de esta marca.
              </p>
              {error && (
                <p role="alert" className="mt-4 rounded-md bg-red-50 px-3 py-2.5 text-[14px] text-red-700">
                  {error}
                </p>
              )}
              <div className="mt-4">
                <RebillCardPayment
                  planId={fase.plan}
                  couponCode={null}
                  prefetchedCtx={{
                    companyCtx: { idCompany: ctx.idCompany, userId: ctx.userId },
                    customerPrefill: ctx.email || ctx.nombre
                      ? { ...(ctx.email ? { email: ctx.email } : {}), ...(ctx.nombre ? { fullName: ctx.nombre } : {}) }
                      : null,
                  }}
                  metadataExtra={fase.metadata}
                  onEstado={(estado) => {
                    if (sesionDelRender !== sesion.current) return;
                    tarjetaEnviada.current = tarjetaEnCamino(tarjetaEnviada.current, estado);
                    setHayTarjetaEnviada(tarjetaEnviada.current);
                    if (estado === 'tarjeta_enviada') setProcesandoTarjeta(true);
                    else if (estado === 'rechazo') setProcesandoTarjeta(false);
                  }}
                  onSuccess={() => {
                    // Un éxito de un checkout de otra apertura (u otra marca) no escribe acá.
                    if (sesionDelRender !== sesion.current || !brandId) return;
                    setProcesandoTarjeta(false);
                    void esperarElPlan(sesionDelRender, brandId, fase.plan, false);
                  }}
                  onError={(m) => {
                    if (sesionDelRender !== sesion.current) return;
                    setProcesandoTarjeta(false);
                    setError(m || 'No pudimos procesar la tarjeta. Probá con otra.');
                  }}
                />
              </div>
              <button
                type="button"
                disabled={procesandoTarjeta}
                onClick={() => {
                  setError(null);
                  // Una tarjeta enviada sin respuesta pudo haber cobrado: se verifica la billetera.
                  if (alSalirDelCheckout({ tarjetaEnviada: tarjetaEnviada.current }) === 'verificar') {
                    if (brandId) void esperarElPlan(sesion.current, brandId, fase.plan, true);
                    return;
                  }
                  setFase({ tipo: 'vista' });
                }}
                className="mt-2 w-full rounded-md px-5 py-2.5 text-[14px] font-medium text-black/55 transition-colors hover:bg-black/[0.03] disabled:opacity-40"
              >
                {hayTarjetaEnviada ? 'Ver si el pago entró' : 'Volver a los planes'}
              </button>
            </div>
          )}

          {ctx && fase.tipo === 'esperando' && (
            <div key="esperando" className="animate-paso-adelante flex flex-col items-center py-10 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
              <p className="mt-4 text-[16px] font-semibold text-brand-black">
                {fase.verificando ? 'Verificando el cobro…' : `Activando el plan de ${ctx.marca}…`}
              </p>
              <p className="mt-1 text-[14px] text-black/55">Tarda unos segundos. No cierres esta ventana.</p>
            </div>
          )}

          {fase.tipo === 'sin_confirmar' && (
            <div key="sin_confirmar" className="animate-paso-adelante py-6">
              <p className="pr-8 text-[17px] font-semibold text-brand-black">No pudimos confirmar el cobro</p>
              <p className="mt-2 text-[15px] text-black/60">
                No pudimos confirmar el cobro. Si en unos minutos no ves el plan en esta marca, escribinos.
              </p>
              <BotonPrincipal onClick={onListo} className="mt-6 w-full">
                Entendido
              </BotonPrincipal>
            </div>
          )}

          {ctx && fase.tipo === 'listo' && (
            <div key="listo" className="animate-paso-adelante flex flex-col items-center py-6 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-600">
                <Check className="h-6 w-6" />
              </span>
              <p className="mt-4 text-[17px] font-semibold text-brand-black">
                {fase.confirmado ? `Listo: ${ctx.marca} ya tiene su plan` : 'El pago entró'}
              </p>
              <p className="mt-1 text-[14px] text-black/55">
                {fase.confirmado
                  ? 'Ya podés seguir creando con los créditos de esta marca.'
                  : 'El plan puede tardar unos minutos en aparecer. Si no lo ves, escribinos.'}
              </p>
              {fase.confirmado && recargaAlTerminar && ctx.esDueno && ctx.recarga && brandId && (
                <div className="mt-5 w-full text-left animate-fade-in">
                  <RecargaAutomatica
                    brandId={brandId}
                    recarga={ctx.recarga}
                    devTester={ctx.devTester}
                    variante="linea"
                    onCambio={(r) => setCtx((c) => (c ? { ...c, recarga: r } : c))}
                  />
                </div>
              )}
              <BotonPrincipal onClick={onListo} className="mt-6 w-full">
                Seguir
              </BotonPrincipal>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Encabezado({
  marca,
  linea,
  titulo,
}: {
  marca: string;
  linea: { texto: string; sinPlan: boolean } | null;
  titulo: string;
}) {
  return (
    <div className="pr-8">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-black/50">
        <span className="font-medium text-black/70">{marca}</span>
        {linea && (
          <span
            className={`rounded-sm px-1.5 py-0.5 text-[12px] font-medium ${
              linea.sinPlan ? 'bg-amber-50 text-amber-800' : 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary'
            }`}
          >
            {linea.texto}
          </span>
        )}
      </p>
      <h2 id="titulo-plan-de-marca" className="mt-1.5 text-[19px] font-semibold leading-[26px] text-brand-black">
        {titulo}
      </h2>
    </div>
  );
}

function BotonPrincipal({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-brand-primary px-6 py-3 text-[15px] font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-brand-primary-hover enabled:active:scale-[0.96] disabled:opacity-60 ${className}`}
    >
      {children}
    </button>
  );
}
